import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function firstText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string') {
      const text = value.trim();
      if (text) return text;
      continue;
    }
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'boolean') return String(value);
  }
  return '';
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isNaN(parsed) ? null : parsed;
}

function toBooleanOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const text = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(text)) return true;
  if (['false', '0', 'no', 'n'].includes(text)) return false;
  return null;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => String(item?.uuid ?? item?.id ?? '').trim() === String(targetUuid ?? '').trim())
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (
      candidate.uuid ||
      candidate.id ||
      candidate.state ||
      candidate.pathogen ||
      candidate.disease_event_name ||
      candidate.diseaseEventName ||
      candidate.species_uuid ||
      candidate.mortality_count
    ) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage = 25, collectionKey }) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    return extractCollection(data, normalize);
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize);
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return data;
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'submit', uuid });
      return item;
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'validate', uuid });
      return item;
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'approve', uuid });
      return item;
    },

    async reject(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/reject`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'reject', uuid });
      return item;
    },
  };
}

const ACRONYM_MAP = {
  amr: 'AMR',
  asf: 'ASF',
  fmd: 'FMD',
  hpai: 'HPAI',
  lsd: 'LSD',
  ncd: 'NCD',
  ncdc: 'NCDC',
  naits: 'NAITS',
  ngn: 'NGN',
  ppr: 'PPR',
  rabies: 'Rabies',
};

function humanizeText(value) {
  const text = trimOrNull(value);
  if (!text) return '';

  const compact = text.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(compact)) {
    return compact;
  }
  const lower = compact.toLowerCase();

  if (ACRONYM_MAP[lower]) return ACRONYM_MAP[lower];
  if (/^[a-z]{2,5}$/.test(lower)) return lower.toUpperCase();

  return compact
    .split(' ')
    .map((part) => {
      const lowerPart = part.toLowerCase();
      if (ACRONYM_MAP[lowerPart]) return ACRONYM_MAP[lowerPart];
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
}

function normalizeDiagnosisBasis(value) {
  const text = trimOrNull(value);
  if (!text) return '';
  const key = text.toLowerCase().replace(/[\s-]+/g, '_');

  const map = {
    field_diagnosis: 'Field diagnosis',
    lab_confirmed: 'Lab confirmed',
    laboratory_confirmed: 'Lab confirmed',
    suspected: 'Suspected',
    unknown: 'Unknown',
  };

  return map[key] ?? humanizeText(text);
}

function normalizeRiskFlag(value) {
  const bool = toBooleanOrNull(value);
  if (bool === true) return 'Risk Flag';
  if (bool === false) return 'No Risk';
  return humanizeText(value);
}

function normalizePercentDisplay(value, decimals = 0) {
  if (value === null || value === undefined || value === '') return '';

  const text = String(value).trim();
  if (!text) return '';
  if (text.endsWith('%')) return text;

  const numeric = Number(text.replace(/,/g, ''));
  if (!Number.isFinite(numeric)) return text;

  const fixed = numeric.toFixed(decimals);
  return `${fixed.replace(/\.0+$/, '')}%`;
}

function computePercent(numerator, denominator, decimals = 0) {
  const num = toNumberOrNull(numerator);
  const den = toNumberOrNull(denominator);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return '';

  const value = (num / den) * 100;
  const fixed = value.toFixed(decimals);
  return `${fixed.replace(/\.0+$/, '')}%`;
}

function normalizeDateValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const text = String(value).trim();
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const dmyMatch = text.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyMatch) {
    const [, first, second, year] = dmyMatch;
    const a = Number(first);
    const b = Number(second);

    const month = a > 12 ? b : a;
    const day = a > 12 ? a : b > 12 ? a : b;

    const date = new Date(Number(year), month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, first, second, year] = slashMatch;
    const a = Number(first);
    const b = Number(second);

    const month = a > 12 ? b : a;
    const day = a > 12 ? a : b > 12 ? a : b;

    const date = new Date(Number(year), month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateDisplay(value) {
  const date = normalizeDateValue(value);
  if (!date) {
    const text = trimOrNull(value);
    return text || '-';
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function formatMonthYear(value) {
  const date = normalizeDateValue(value);
  if (!date) {
    const text = trimOrNull(value);
    return text || '-';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

function formatDateInput(value) {
  const date = normalizeDateValue(value);
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatCurrencyNgn(value) {
  if (value === null || value === undefined || value === '') return '';
  const numeric = toNumberOrNull(value);
  if (numeric === null) {
    const text = trimOrNull(value);
    return text || '';
  }

  return `NGN ${numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function extractDisplayText(item) {
  if (item === null || item === undefined) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (typeof item !== 'object') return '';

  return firstText(
    item.label,
    item.name,
    item.title,
    item.code,
    item.value,
    item.uuid,
    item.id,
  );
}

function normalizeListDisplay(value, transform = (text) => text) {
  if (value === null || value === undefined || value === '') return '';

  const items = Array.isArray(value) ? value : [value];
  const next = items
    .map((item) => transform(extractDisplayText(item)))
    .map((text) => trimOrNull(text))
    .filter(Boolean);

  return next.join(', ');
}

function normalizePayloadArray(value) {
  if (!Array.isArray(value)) {
    const text = trimOrNull(value);
    return text ? [text] : [];
  }

  return value
    .map((item) => {
      if (item === null || item === undefined) return null;
      if (typeof item === 'object') {
        return trimOrNull(item.uuid ?? item.id ?? item.value ?? item.code ?? item.label ?? item.name);
      }
      return trimOrNull(item);
    })
    .filter(Boolean);
}

function normalizeWorkflowStatus(row = {}) {
  return trimOrNull(row.data_status ?? row.dataStatus ?? row.workflow_status ?? row.workflowStatus ?? row.status) || 'draft';
}

function normalizeOutbreakRecord(row = {}) {
  const diseaseEventName = firstText(
    row.disease_event_name,
    row.diseaseEventName,
    row.outbreak_reference,
    row.outbreakReference,
    row.outbreak_record_number,
    row.outbreakRecordNumber,
    row.reference_number,
    row.referenceNumber,
    row.uuid,
  );

  const detectionDateSource = firstText(
    row.detection_date,
    row.detectionDate,
    row.start_date,
    row.startDate,
    row.onset_date,
    row.onsetDate,
    row.reported_at,
    row.reportedAt,
  );

  const endDateSource = firstText(
    row.end_date,
    row.endDate,
    row.resolution_date,
    row.resolutionDate,
    row.closed_at,
    row.closedAt,
  );

  const totalCasesValue = firstText(
    row.total_cases,
    row.totalCases,
    row.case_count,
    row.caseCount,
    row.affected_count,
    row.affectedCount,
    row.animal_cases,
    row.animalCases,
  );

  const totalDeathsValue = firstText(
    row.total_deaths,
    row.totalDeaths,
    row.death_count,
    row.deathCount,
    row.deaths,
  );

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    outbreakRef: diseaseEventName,
    diseaseEventName,
    pathogen: humanizeText(row.pathogen ?? row.pathogen_code ?? row.pathogenCode),
    startDate: formatDateDisplay(detectionDateSource),
    endDate: formatDateDisplay(endDateSource),
    stateAffected: firstText(row.state, row.state_name, row.stateName, row.state_affected, row.stateAffected),
    lgasAffected: normalizeListDisplay(row.lgas_affected ?? row.lgasAffected ?? row.lga_affected ?? row.lgaAffected ?? row.lgas),
    totalCases: toNumberOrNull(totalCasesValue),
    totalDeaths: toNumberOrNull(totalDeathsValue),
    responseActions: normalizeListDisplay(
      row.response_actions ?? row.responseActions ?? row.actions_taken ?? row.actionsTaken,
      humanizeText,
    ),
    zoonoticRiskFlag: normalizeRiskFlag(row.zoonotic_risk_flag ?? row.zoonoticRiskFlag),
    economicLoss: formatCurrencyNgn(
      row.economic_loss_estimate_ngn ??
      row.economicLossEstimateNgn ??
      row.economic_loss ??
      row.economicLoss
    ),
    alertLevel: humanizeText(row.alert_level ?? row.alertLevel),
    data_status: normalizeWorkflowStatus(row),
    dataStatus: normalizeWorkflowStatus(row),
    workflow_status: row.workflow_status ?? row.workflowStatus ?? null,
    workflowStatus: row.workflow_status ?? row.workflowStatus ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildOutbreakRecordPayload(payload = {}) {
  const source = payload.raw ?? payload.existing ?? payload.original ?? {};

  return {
    state: trimOrNull(payload.state ?? source.state),
    disease_event_name: trimOrNull(
      payload.disease_event_name ??
      payload.diseaseEventName ??
      payload.outbreakRef ??
      source.disease_event_name ??
      source.diseaseEventName ??
      source.outbreakRef
    ),
    pathogen: trimOrNull(payload.pathogen ?? source.pathogen),
    alert_level: trimOrNull(payload.alert_level ?? payload.alertLevel ?? source.alert_level ?? source.alertLevel),
    detection_date: formatDateInput(payload.detection_date ?? payload.detectionDate ?? payload.startDate ?? source.detection_date ?? source.startDate),
    end_date: formatDateInput(payload.end_date ?? payload.endDate ?? source.end_date ?? source.endDate) ?? null,
    lgas_affected: normalizePayloadArray(payload.lgas_affected ?? payload.lgasAffected ?? source.lgas_affected ?? source.lgasAffected),
    response_actions: normalizePayloadArray(payload.response_actions ?? payload.responseActions ?? source.response_actions ?? source.responseActions),
    zoonotic_risk_flag: toBooleanOrNull(payload.zoonotic_risk_flag ?? payload.zoonoticRiskFlag ?? source.zoonotic_risk_flag ?? source.zoonoticRiskFlag),
    economic_loss_estimate_ngn: toNumberOrNull(
      payload.economic_loss_estimate_ngn ??
      payload.economicLossEstimateNgn ??
      payload.economicLoss ??
      source.economic_loss_estimate_ngn ??
      source.economicLossEstimateNgn ??
      source.economicLoss
    ),
  };
}

function normalizeVaccinationRecord(row = {}) {
  const speciesTargeted = row.species_targeted ?? row.speciesTargeted ?? row.species ?? row.species_label ?? row.speciesLabel;
  const species = normalizeListDisplay(speciesTargeted, humanizeText);
  const state = firstText(row.state, row.state_name, row.stateName);
  const lga = firstText(row.lga, row.lga_name, row.lgaName);
  const stateLga = firstText(row.state_lga, row.stateLGA, row.state_lga_label);
  const targetPopulation = toNumberOrNull(
    firstText(row.target_population, row.targetPopulation, row.target_pop, row.targetPop)
  );
  const animalsVaccinated = toNumberOrNull(
    firstText(row.animals_vaccinated, row.animalsVaccinated, row.vaccinated_count, row.vaccinatedCount)
  );

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    diseaseTarget: humanizeText(row.disease_target ?? row.diseaseTarget),
    species,
    stateLGA: firstText(stateLga, state && lga ? `${state} / ${lga}` : state || lga),
    targetPop: targetPopulation,
    animalsVaccinated,
    coverageRate:
      normalizePercentDisplay(row.coverage_rate ?? row.coverageRate, 0) ||
      computePercent(animalsVaccinated, targetPopulation, 0),
    doses: toNumberOrNull(firstText(row.doses_administered, row.dosesAdministered, row.doses)),
    batchNo: firstText(row.vaccine_batch_number, row.vaccineBatchNumber, row.batch_number, row.batchNo),
    institution: firstText(
      row.implementing_institution_name,
      row.implementingInstitutionName,
      row.institution_name,
      row.institutionName,
      row.implementing_institution_label,
      row.institution_label,
      row.implementing_institution_uuid,
      row.institution_uuid,
    ),
    coldChainStatus: humanizeText(row.cold_chain_status ?? row.coldChainStatus),
    campaignStatus: humanizeText(row.campaign_status ?? row.campaignStatus),
    coldChainCompliance:
      normalizePercentDisplay(row.cold_chain_compliance ?? row.coldChainCompliance ?? row.cold_chain_compliance_pct ?? row.coldChainCompliancePct, 0),
    state,
    lga,
    campaignStartDate: formatDateDisplay(row.campaign_start_date ?? row.campaignStartDate),
    campaignEndDate: formatDateDisplay(row.campaign_end_date ?? row.campaignEndDate),
    implementingInstitutionUuid: row.implementing_institution_uuid ?? row.implementingInstitutionUuid ?? null,
    data_status: normalizeWorkflowStatus(row),
    dataStatus: normalizeWorkflowStatus(row),
    workflow_status: row.workflow_status ?? row.workflowStatus ?? null,
    workflowStatus: row.workflow_status ?? row.workflowStatus ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildVaccinationRecordPayload(payload = {}) {
  const source = payload.raw ?? payload.existing ?? payload.original ?? {};
  const speciesTargeted = normalizePayloadArray(
    payload.species_targeted ??
    payload.speciesTargeted ??
    source.species_targeted ??
    source.speciesTargeted
  );

  return {
    state: trimOrNull(payload.state ?? source.state),
    disease_target: trimOrNull(payload.disease_target ?? payload.diseaseTarget ?? source.disease_target ?? source.diseaseTarget),
    species_targeted: speciesTargeted,
    target_population: toNumberOrNull(payload.target_population ?? payload.targetPopulation ?? source.target_population ?? source.targetPopulation),
    animals_vaccinated: toNumberOrNull(payload.animals_vaccinated ?? payload.animalsVaccinated ?? source.animals_vaccinated ?? source.animalsVaccinated),
    doses_administered: toNumberOrNull(payload.doses_administered ?? payload.dosesAdministered ?? source.doses_administered ?? source.dosesAdministered),
    vaccine_batch_number: trimOrNull(payload.vaccine_batch_number ?? payload.vaccineBatchNumber ?? source.vaccine_batch_number ?? source.vaccineBatchNumber),
    cold_chain_status: trimOrNull(payload.cold_chain_status ?? payload.coldChainStatus ?? source.cold_chain_status ?? source.coldChainStatus),
    implementing_institution_uuid: trimOrNull(
      payload.implementing_institution_uuid ??
      payload.implementingInstitutionUuid ??
      source.implementing_institution_uuid ??
      source.implementingInstitutionUuid
    ),
    campaign_status: trimOrNull(payload.campaign_status ?? payload.campaignStatus ?? source.campaign_status ?? source.campaignStatus),
    campaign_start_date: formatDateInput(payload.campaign_start_date ?? payload.campaignStartDate ?? source.campaign_start_date ?? source.campaignStartDate),
    campaign_end_date: formatDateInput(payload.campaign_end_date ?? payload.campaignEndDate ?? source.campaign_end_date ?? source.campaignEndDate),
  };
}

function normalizeMortalityRecord(row = {}) {
  const state = firstText(row.state, row.state_name, row.stateName);
  const lga = firstText(row.lga, row.lga_name, row.lgaName);
  const populationBase = toNumberOrNull(
    firstText(row.population_base, row.populationBase, row.base_population, row.basePopulation)
  );
  const mortalityCount = toNumberOrNull(
    firstText(row.mortality_count, row.mortalityCount, row.death_count, row.deathCount)
  );
  const mortalityRate =
    normalizePercentDisplay(row.mortality_rate ?? row.mortalityRate, 1) ||
    computePercent(mortalityCount, populationBase, 1);

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    species: firstText(
      row.species_label,
      row.speciesLabel,
      row.species_name,
      row.speciesName,
      row.species_code,
      row.speciesCode,
      row.species_uuid,
      row.speciesUuid,
      row.species?.name,
      row.species?.label,
      row.species?.species_code,
      row.species?.speciesCode,
    ) ? humanizeText(firstText(
      row.species_label,
      row.speciesLabel,
      row.species_name,
      row.speciesName,
      row.species_code,
      row.speciesCode,
      row.species_uuid,
      row.speciesUuid,
      row.species?.name,
      row.species?.label,
      row.species?.species_code,
      row.species?.speciesCode,
    )) : '',
    state,
    refPeriod: firstText(
      row.reference_period,
      row.referencePeriod,
      row.period,
      row.period_label,
      row.periodLabel,
    ) || formatMonthYear(row.onset_date ?? row.onsetDate ?? row.start_date ?? row.startDate),
    mortalityCount,
    probableCause: firstText(row.pathogen, row.pathogen_label, row.cause_of_death, row.causeOfDeath, row.probable_cause, row.probableCause),
    diseaseEvent: firstText(
      row.outbreak_record_label,
      row.outbreakRecordLabel,
      row.outbreak_record_name,
      row.outbreakRecordName,
      row.outbreak_record_uuid,
      row.outbreakRecordUuid,
    ),
    lga,
    mortalityRate,
    populationBase,
    productionSystem: humanizeText(row.production_system ?? row.productionSystem),
    ageClassAtDeath: firstText(
      row.age_class_label,
      row.ageClassLabel,
      row.age_class_name,
      row.ageClassName,
      row.age_class,
      row.ageClass,
      row.age_class_code,
      row.ageClassCode,
      row.age_class_uuid,
      row.ageClassUuid,
    ),
    confirmedVsSuspected: normalizeDiagnosisBasis(row.diagnosis_basis ?? row.diagnosisBasis),
    disposalMethod: humanizeText(row.disposal_method ?? row.disposalMethod),
    thresholdAlert:
      humanizeText(row.threshold_alert ?? row.thresholdAlert) ||
      (Number.parseFloat(String(mortalityRate).replace('%', '')) >= 5 ? 'Alert' : 'Normal'),
    onsetDate: formatDateDisplay(row.onset_date ?? row.onsetDate),
    endDate: formatDateDisplay(row.end_date ?? row.endDate),
    speciesUuid: row.species_uuid ?? row.speciesUuid ?? null,
    ageClassUuid: row.age_class_uuid ?? row.ageClassUuid ?? null,
    outbreakRecordUuid: row.outbreak_record_uuid ?? row.outbreakRecordUuid ?? null,
    reportingInstitutionUuid: row.reporting_institution_uuid ?? row.reportingInstitutionUuid ?? null,
    data_status: normalizeWorkflowStatus(row),
    dataStatus: normalizeWorkflowStatus(row),
    workflow_status: row.workflow_status ?? row.workflowStatus ?? null,
    workflowStatus: row.workflow_status ?? row.workflowStatus ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildMortalityRecordPayload(payload = {}) {
  const source = payload.raw ?? payload.existing ?? payload.original ?? {};

  return {
    state: trimOrNull(payload.state ?? source.state),
    species_uuid: trimOrNull(payload.species_uuid ?? payload.speciesUuid ?? source.species_uuid ?? source.speciesUuid),
    production_system: trimOrNull(payload.production_system ?? payload.productionSystem ?? source.production_system ?? source.productionSystem),
    pathogen: trimOrNull(payload.pathogen ?? source.pathogen),
    outbreak_record_uuid: trimOrNull(payload.outbreak_record_uuid ?? payload.outbreakRecordUuid ?? source.outbreak_record_uuid ?? source.outbreakRecordUuid),
    onset_date: formatDateInput(payload.onset_date ?? payload.onsetDate ?? source.onset_date ?? source.onsetDate),
    end_date: formatDateInput(payload.end_date ?? payload.endDate ?? source.end_date ?? source.endDate),
    population_base: toNumberOrNull(payload.population_base ?? payload.populationBase ?? source.population_base ?? source.populationBase),
    mortality_count: toNumberOrNull(payload.mortality_count ?? payload.mortalityCount ?? source.mortality_count ?? source.mortalityCount),
    age_class_uuid: trimOrNull(payload.age_class_uuid ?? payload.ageClassUuid ?? source.age_class_uuid ?? source.ageClassUuid),
    diagnosis_basis: trimOrNull(payload.diagnosis_basis ?? payload.diagnosisBasis ?? source.diagnosis_basis ?? source.diagnosisBasis),
    disposal_method: trimOrNull(payload.disposal_method ?? payload.disposalMethod ?? source.disposal_method ?? source.disposalMethod),
    reporting_institution_uuid: trimOrNull(
      payload.reporting_institution_uuid ??
      payload.reportingInstitutionUuid ??
      source.reporting_institution_uuid ??
      source.reportingInstitutionUuid
    ),
  };
}

const outbreakRecordsBaseApi = makeCollectionApi({
  basePath: '/v1/outbreak-records',
  normalize: normalizeOutbreakRecord,
  buildPayload: buildOutbreakRecordPayload,
  defaultPerPage: 25,
});

const vaccinationRecordsBaseApi = makeCollectionApi({
  basePath: '/v1/vaccination-records',
  normalize: normalizeVaccinationRecord,
  buildPayload: buildVaccinationRecordPayload,
  defaultPerPage: 25,
});

const mortalityRecordsBaseApi = makeCollectionApi({
  basePath: '/v1/mortality-records',
  normalize: normalizeMortalityRecord,
  buildPayload: buildMortalityRecordPayload,
  defaultPerPage: 25,
});

export const outbreakRecordsApi = withOfflineCollectionSupport(outbreakRecordsBaseApi, {
  basePath: '/v1/outbreak-records',
  collectionKey: 'outbreak-records',
  normalize: normalizeOutbreakRecord,
  buildPayload: buildOutbreakRecordPayload,
});

export const vaccinationRecordsApi = withOfflineCollectionSupport(vaccinationRecordsBaseApi, {
  basePath: '/v1/vaccination-records',
  collectionKey: 'vaccination-records',
  normalize: normalizeVaccinationRecord,
  buildPayload: buildVaccinationRecordPayload,
});

export const mortalityRecordsApi = withOfflineCollectionSupport(mortalityRecordsBaseApi, {
  basePath: '/v1/mortality-records',
  collectionKey: 'mortality-records',
  normalize: normalizeMortalityRecord,
  buildPayload: buildMortalityRecordPayload,
});

export {
  normalizeOutbreakRecord,
  buildOutbreakRecordPayload,
  normalizeVaccinationRecord,
  buildVaccinationRecordPayload,
  normalizeMortalityRecord,
  buildMortalityRecordPayload,
};
